import { useEffect, useState } from 'react';
import Link from 'next/link';
import { Calendar, Plus, Loader2, Lock, Edit, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth.store';
import { API_BASE } from '@/lib/api';
import { Button } from '@/components/ui/button';

interface Event {
  id: string;
  name: string;
  description: string | null;
  category: string;
  format: string;
  tier: string;
  status: string;
  entryFee: number;
  entryFeeType: string;
  prizePool: number;
  currentEntries: number;
  maxEntries: number | null;
  startTime: string;
  endTime: string;
  entryCount: number;
  isLocked: boolean;
}

const STATUS_COLORS: Record<string, string> = {
  UPCOMING: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
  OPEN: 'bg-green-500/10 text-green-400 border-green-500/30',
  LOCKED: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30',
  LIVE: 'bg-red-500/10 text-red-400 border-red-500/30',
  SCORING: 'bg-purple-500/10 text-purple-400 border-purple-500/30',
  COMPLETED: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/30',
};

export default function AdminEventsPage() {
  const { accessToken } = useAuthStore();
  const [events, setEvents] = useState<Event[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    fetchEvents();
  }, [accessToken, statusFilter]);

  const fetchEvents = async () => {
    if (!accessToken) return;

    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      
      const res = await fetch(`${API_BASE}/api/admin/events?${params.toString()}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      
      if (res.ok) {
        const data = await res.json();
        setEvents(data.events);
      }
    } catch (error) {
      console.error('Failed to fetch events:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Are you sure you want to delete "${name}"?`)) return;

    try {
      const res = await fetch(`${API_BASE}/api/admin/events/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (res.ok) {
        setEvents((prev) => prev.filter((e) => e.id !== id));
      } else {
        const data = await res.json();
        alert(data.error || 'Failed to delete event');
      }
    } catch (error) {
      alert('Failed to delete event');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Calendar className="h-6 w-6 text-red-400" />
          <h1 className="text-2xl font-bold text-white">Events</h1>
        </div>
        <Link href="/admin/events/new">
          <Button>
            <Plus className="h-4 w-4" />
            Create Event
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-6">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white text-sm focus:border-red-500 focus:outline-none"
        >
          <option value="">All Statuses</option>
          <option value="UPCOMING">Upcoming</option>
          <option value="OPEN">Open</option>
          <option value="LOCKED">Locked</option>
          <option value="LIVE">Live</option>
          <option value="SCORING">Scoring</option>
          <option value="COMPLETED">Completed</option>
        </select>
      </div>

      {/* Events Table */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 text-red-400 animate-spin" />
        </div>
      ) : events.length === 0 ? (
        <div className="text-center py-12 bg-zinc-900 rounded-xl border border-zinc-800">
          <Calendar className="h-12 w-12 text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-400">No events found</p>
          <Link href="/admin/events/new" className="text-red-400 hover:text-red-300 text-sm mt-2 inline-block">
            Create your first event
          </Link>
        </div>
      ) : (
        <div className="bg-zinc-900 rounded-xl border border-zinc-800 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="text-left p-4 text-sm font-medium text-zinc-400">Event</th>
                <th className="text-left p-4 text-sm font-medium text-zinc-400">Category</th>
                <th className="text-left p-4 text-sm font-medium text-zinc-400">Status</th>
                <th className="text-left p-4 text-sm font-medium text-zinc-400">Entries</th>
                <th className="text-left p-4 text-sm font-medium text-zinc-400">Prize Pool</th>
                <th className="text-left p-4 text-sm font-medium text-zinc-400">Start</th>
                <th className="text-right p-4 text-sm font-medium text-zinc-400">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {events.map((event) => (
                <tr key={event.id} className="hover:bg-zinc-800/50">
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      {event.isLocked && (
                        <span title="Locked - has entries">
                          <Lock className="h-4 w-4 text-yellow-500" />
                        </span>
                      )}
                      <div>
                        <p className="font-medium text-white">{event.name}</p>
                        <p className="text-xs text-zinc-500">{event.format} • {event.tier}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className="text-sm text-zinc-300">{event.category}</span>
                  </td>
                  <td className="p-4">
                    <span className={cn(
                      'px-2 py-1 rounded text-xs font-medium border',
                      STATUS_COLORS[event.status] || STATUS_COLORS.UPCOMING
                    )}>
                      {event.status}
                    </span>
                  </td>
                  <td className="p-4">
                    <span className="text-sm text-zinc-300">
                      {event.entryCount}
                      {event.maxEntries && ` / ${event.maxEntries}`}
                    </span>
                  </td>
                  <td className="p-4">
                    <span className="text-sm text-green-400">
                      {event.prizePool.toLocaleString()} coins
                    </span>
                  </td>
                  <td className="p-4">
                    <span className="text-sm text-zinc-400">
                      {new Date(event.startTime).toLocaleDateString()}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center justify-end gap-2">
                      <Link
                        href={`/admin/events/${event.id}`}
                        className="p-2 rounded-lg hover:bg-zinc-700 text-zinc-400 hover:text-white transition-colors"
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </Link>
                      <button
                        onClick={() => handleDelete(event.id, event.name)}
                        disabled={event.isLocked}
                        className={cn(
                          'p-2 rounded-lg transition-colors',
                          event.isLocked
                            ? 'text-zinc-600 cursor-not-allowed'
                            : 'hover:bg-red-500/10 text-zinc-400 hover:text-red-400'
                        )}
                        title={event.isLocked ? 'Cannot delete - has entries' : 'Delete'}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
